import React, { useEffect, useRef, useState } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import AutoGrowInput from './AutoGrowInput';

/**
 * NodeView درون‌خطی برای کسر — مثل Equation ورد.
 *
 * عرض ستون کسر در خود NodeView محاسبه می‌شود:
 * colWidth = max(عرض متن صورت، عرض متن مخرج، حداقل یک کاراکتر)
 * پس تغییر «هر کدام» — صورت یا مخرج — عرض کل و خط کسر را عوض می‌کند.
 */
const FractionNodeView = ({ node, updateAttributes, selected }) => {
  const { num = '', den = '', whole = '' } = node.attrs;
  const isMixed = node.type.name === 'mixedFraction';
  const numRef = useRef(null);
  const numMeasureRef = useRef(null);
  const denMeasureRef = useRef(null);
  // draft های زنده — عرض ستون حین تایپ هم به‌روز شود (قبل از کامیت)
  const [numDraft, setNumDraft] = useState(num);
  const [denDraft, setDenDraft] = useState(den);
  const [colWidth, setColWidth] = useState(undefined);

  useEffect(() => setNumDraft(num), [num]);
  useEffect(() => setDenDraft(den), [den]);

  const recompute = () => {
    if (!numMeasureRef.current || !denMeasureRef.current) return;
    const fs = parseFloat(getComputedStyle(numMeasureRef.current).fontSize);
    const w = Math.max(
      numMeasureRef.current.offsetWidth,
      denMeasureRef.current.offsetWidth,
      Math.round(fs * 0.75)
    );
    setColWidth(w);
  };

  // وابسته به draft ها: با هر کلید، عرض ستون/خط کسر همان لحظه رشد می‌کند
  useEffect(recompute, [num, den, numDraft, denDraft]);

  // فوکوس خودکار اسلات اول هنگام درج خالی
  React.useEffect(() => {
    if (!num && !den && numRef.current) {
      numRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <NodeViewWrapper
      as="span"
      className={`relative inline-block align-middle mx-1 rounded transition-all ${
        selected ? 'bg-blue-50/40 ring-2 ring-blue-400' : ''
      }`}
      style={{ direction: 'ltr', unicodeBidi: 'isolate' }}
    >
      {/* دو span مخفی فقط برای اندازه‌گیری — از draft زنده استفاده می‌کنند */}
      <span
        ref={numMeasureRef}
        aria-hidden="true"
        style={{ position: 'absolute', visibility: 'hidden', whiteSpace: 'pre', left: 0, top: 0, fontSize: '0.88em' }}
      >
        {numDraft || '?'}
      </span>
      <span
        ref={denMeasureRef}
        aria-hidden="true"
        style={{ position: 'absolute', visibility: 'hidden', whiteSpace: 'pre', left: 0, top: 0, fontSize: '0.88em' }}
      >
        {denDraft || '?'}
      </span>

      <span className="inline-flex items-center" contentEditable={false}>
        {isMixed && (
          <>
            <AutoGrowInput
              value={whole}
              onCommit={(v) => updateAttributes({ whole: v })}
              className="border-b border-transparent focus:border-blue-400"
              aria-label="عدد صحیح"
              style={{ fontSize: '0.88em' }}
            />
            <span style={{ width: '2px' }} aria-hidden="true" />
          </>
        )}

        <span
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: colWidth ? `${colWidth}px` : '0.9em',
            lineHeight: 1.1,
            fontSize: '0.88em',
          }}
        >
          <AutoGrowInput
            ref={numRef}
            fillWidth
            value={num}
            onDraftChange={setNumDraft}
            onCommit={(v) => updateAttributes({ num: v })}
            aria-label="صورت کسر"
          />
          {/* خط کسر — عرضش همیشه = عرض ستون */}
          <span
            aria-hidden="true"
            style={{
              alignSelf: 'stretch',
              height: '1.5px',
              background: '#334155',
              margin: '1px 0',
            }}
          />
          <AutoGrowInput
            fillWidth
            value={den}
            onDraftChange={setDenDraft}
            onCommit={(v) => updateAttributes({ den: v })}
            aria-label="مخرج کسر"
          />
        </span>
      </span>
    </NodeViewWrapper>
  );
};

export default FractionNodeView;
